import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchEventBySlug } from "@/lib/api/events";
import { fetchMatches } from "@/lib/api/likes";
import { getParticipantSession } from "@/lib/participant-session";

import { Card } from "@/components/ui/Card";
import { LinkButton } from "@/components/ui/Button";
import { Logo } from "@/components/ui/Logo";
import { InlineAlert } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";
import { GlobalFooter } from "@/components/layout/GlobalFooter";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function EventWrapPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const session = slug ? getParticipantSession(slug) : null;

  useEffect(() => {
    if (!slug) return;
    if (!session) navigate(`/e/${slug}`, { replace: true });
  }, [slug, session, navigate]);

  const { data: event } = useQuery({
    queryKey: ["event-slug", slug],
    queryFn: () => fetchEventBySlug(slug ?? ""),
    enabled: Boolean(slug),
  });

  const { data: matches = [] } = useQuery({
    queryKey: ["matches", session?.eventId, session?.participantId],
    queryFn: () => fetchMatches(session?.eventId ?? "", session?.participantId ?? ""),
    enabled: Boolean(session?.eventId && session?.participantId),
  });

  if (!session || !event) return null;

  const retentionDate = new Date(
    new Date(event.event_date).getTime() + 30 * 86_400_000,
  );

  return (
    <MobileFrame>
      <div className="flex-1 px-4 py-6 animate-fade-in">
        <Card padding="lg" className="text-center">
          <div className="text-5xl">🎉</div>
          <h1 className="mt-3 text-[22px] font-bold tracking-tight text-[#1a1a1a]">
            {event.title} — that's a wrap
          </h1>
          <p className="mt-1 text-[13px] text-[#6c757d]">{formatDate(event.event_date)}</p>

          <div className="mt-6 flex flex-col gap-3 text-left">
            <SummaryRow
              label="Your matches"
              value={`${matches.length} ${matches.length === 1 ? "person" : "people"}`}
            />
            <SummaryRow
              label="Match contacts available until"
              value={formatDate(retentionDate.toISOString())}
            />
          </div>

          <Card tone="flagged" padding="sm" className="mt-5 text-left">
            <p className="text-[13px] text-[#78350f]">
              All profiles, videos, and match data will be{" "}
              <strong>permanently deleted on {formatDate(retentionDate.toISOString())}</strong>.
              Save any contacts you need now.
            </p>
          </Card>

          <InlineAlert tone="success" className="mt-3 text-left">
            Your match list is still accessible — tap Matches below to review contacts and notes.
          </InlineAlert>

          <div className="mt-6 flex flex-col gap-2">
            <LinkButton to={`/e/${slug}/survey`} fullWidth>
              Leave Feedback
            </LinkButton>
            <LinkButton to={`/e/${slug}/matches`} variant="secondary" fullWidth>
              View My Matches
            </LinkButton>
          </div>
        </Card>
      </div>

    </MobileFrame>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-[10px] border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3">
      <span className="text-[13px] text-[#6c757d]">{label}</span>
      <span className="text-[14px] font-bold text-[#1a1a1a]">{value}</span>
    </div>
  );
}
